import {
  AimOutlined,
  ArrowLeftOutlined,
  BulbOutlined,
  CheckCircleFilled,
  CompassOutlined,
  DollarOutlined,
  ExclamationCircleFilled,
  LoadingOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  SoundOutlined,
  TeamOutlined,
  UserOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { Alert, Button, Collapse, Modal, Result, Skeleton, Tag, Tooltip, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getVirtualIpDraft,
  getVirtualIpProposals,
  getVirtualIpTypeRecommendation,
  regenerateVirtualIpProposals,
  selectVirtualIpProposal,
  startVirtualIpProposals,
  type SaasApiError,
  type VirtualIpDraft,
  type VirtualIpProposals,
  type VirtualIpSelectedType,
  type VirtualIpTypeRecommendation,
} from '../../api'
import VirtualIpStepper from './VirtualIpStepper'
import { virtualIpCompletedThrough } from './virtualIpProgress'
import { useResolvedVirtualIpProgress } from './useResolvedVirtualIpProgress'
import { confirmUpstreamEditIfNeeded } from './virtualIpUpstreamEdit'
import { VIRTUAL_IP_FLOW_TOTAL_STEPS } from './virtualIpFlow'
import './virtualIp.css'

/** VISTOERA_VIRTUAL_IP_STEP3_POSITIONING_COMPARISON_UI_V2.
 *
 * Renders the real ``virtual_ip_positioning_proposals_v1`` payload from the
 * backend; every proposal field is read defensively from
 * ``proposals_payload.proposals`` — never invented on the frontend.
 *
 * Business-semantics reminder baked into the UI (not just this comment):
 * STEP 2 already fixed the "大方向" (parent IP-type direction). STEP 3 is
 * NOT a second type-selection step — it compares concrete positioning
 * routes *underneath* that already-confirmed direction. The
 * ``ParentDirectionBanner`` below the stepper exists specifically to keep
 * that hierarchy visually unmistakable, using only the already-persisted
 * STEP 2 selection snapshot (``typeRec.selected.snapshot``) — zero new AI
 * calls, zero frontend re-summarization.
 *
 * V2 also removes the former right-side "查看方案对比" Drawer: every
 * proposal now renders fully expanded in the main grid (no accordion/
 * collapse/hover-to-reveal), so comparison happens directly in the primary
 * visual area. Accent colors (purple/green/orange/blue, one per proposal by
 * position) are decorative memory aids only — the canonical Vistoera
 * "selected" state (check + primary-color border/glow) is intentionally
 * styled independently of accent so a green-accented card being selected
 * can never be confused with "selection = green".
 */

type PageState = 'loading' | 'error' | 'needs_step2' | 'ready'

type ProposalItem = {
  proposal_id: string
  name?: string
  subtitle?: string
  summary?: string
  positioning_label?: string
  one_liner?: string
  persona_traits?: string[]
  content_direction?: string[]
  target_audience?: string
  commercial_direction?: string[] | { categories?: string[]; description?: string }
  platform_strategy?: string[]
  visual_direction?: string
  voice_style?: string
  differentiation?: string
  core_value_proposition?: string
  content_tone?: string[]
  risk_boundaries?: string[]
  tags?: string[]
  trait_scores?: Record<string, number>
  speaking_style_sample?: string
  match_score?: number
  recommendation_reason?: string
  growth_potential?: string
  preview_asset_id?: string
  preview_asset_url?: string
  preview_generation_status?: string
  [key: string]: unknown
}

const POLL_INTERVAL_MS = 3000

/** One accent per card position — purely a visual memory aid (spec §8).
 * Real proposal count is schema-fixed at exactly 4
 * (``output.schema.json`` proposals: minItems=4, maxItems=4), one more
 * than the 3 illustrated in the reference mock, so a 4th accent (blue) is
 * added here as a consistent extension of the same existing design-token
 * family (--primary / --success / --warning / --info) rather than a new
 * ad-hoc color. */
const PROPOSAL_ACCENTS = ['a', 'b', 'c', 'd'] as const
function accentForIndex(idx: number): string {
  return PROPOSAL_ACCENTS[idx % PROPOSAL_ACCENTS.length]
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && v.length > 0) : []
}

function formatCommercialDirection(value: ProposalItem['commercial_direction']): string {
  if (Array.isArray(value)) return value.join('、')
  if (value && typeof value === 'object') {
    const desc = typeof value.description === 'string' ? value.description : ''
    const cats = asStringArray(value.categories)
    if (desc && cats.length > 0) return `${cats.join('、')} — ${desc}`
    return desc || cats.join('、')
  }
  return ''
}

function parseProposals(payload: Record<string, unknown> | null | undefined): ProposalItem[] {
  if (!payload || !Array.isArray(payload.proposals)) return []
  return payload.proposals
    .map((raw, idx) => {
      if (!raw || typeof raw !== 'object') return null
      const p = raw as Record<string, unknown>
      const proposalId = typeof p.proposal_id === 'string' ? p.proposal_id : `proposal-${idx}`
      return { ...p, proposal_id: proposalId } as ProposalItem
    })
    .filter((p): p is ProposalItem => p !== null)
}

function resolveIpTypeDisplay(
  typeRec: VirtualIpTypeRecommendation | null,
  payload: Record<string, unknown>,
): string | null {
  const fromPayload = payload.selected_ip_type_display_name
  if (typeof fromPayload === 'string' && fromPayload) return fromPayload
  const snap = typeRec?.selected?.snapshot
  if (snap && typeof snap.display_name === 'string' && snap.display_name) return snap.display_name
  if (typeof typeRec?.selected?.ip_type === 'string' && typeRec.selected.ip_type) return typeRec.selected.ip_type
  const fromType = payload.selected_ip_type
  if (typeof fromType === 'string' && fromType) return fromType
  return null
}

function isBusyStatus(status: string) {
  return status === 'QUEUED' || status === 'RUNNING' || status === 'REGENERATING'
}

function proposalStatusBadge(status: string, t: (key: string) => string) {
  switch (status) {
    case 'NOT_STARTED':
      return { text: t('common.pendingGenerate'), className: 'is-neutral' }
    case 'QUEUED':
    case 'RUNNING':
    case 'REGENERATING':
      return { text: t('jobStatus.generating'), className: 'is-info' }
    case 'COMPLETED':
      return { text: t('jobStatus.completed'), className: 'is-success' }
    case 'FAILED':
      return { text: t('jobStatus.failed'), className: 'is-error' }
    case 'STALE':
      return { text: t('jobStatus.needsRegenerate'), className: 'is-warning' }
    default:
      return { text: t('common.pendingGenerate'), className: 'is-neutral' }
  }
}

export default function VirtualIpStep3Page() {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const { workspaceId = '', projectId = '' } = useParams()
  const navigate = useNavigate()

  const [draft, setDraft] = useState<VirtualIpDraft | null>(null)
  const progress = useResolvedVirtualIpProgress()
  const [typeRec, setTypeRec] = useState<VirtualIpTypeRecommendation | null>(null)
  const [proposals, setProposals] = useState<VirtualIpProposals | null>(null)
  const [pageState, setPageState] = useState<PageState>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [selectingId, setSelectingId] = useState<string | null>(null)

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchProposals = useCallback(
    (draftId: string) => {
      return getVirtualIpProposals(workspaceId, draftId).then((r) => {
        setProposals(r.data)
        return r.data
      })
    },
    [workspaceId],
  )

  const schedulePoll = useCallback(
    (draftId: string) => {
      if (pollTimer.current) clearTimeout(pollTimer.current)
      pollTimer.current = setTimeout(() => {
        fetchProposals(draftId)
          .then((data) => {
            if (isBusyStatus(data.status)) schedulePoll(draftId)
          })
          .catch(() => schedulePoll(draftId))
      }, POLL_INTERVAL_MS)
    },
    [fetchProposals],
  )

  useEffect(() => {
    if (!workspaceId) return
    let cancelled = false
    setPageState('loading')
    setLoadError(null)
    getVirtualIpDraft(workspaceId, projectId)
      .then((dRes) => {
        if (cancelled) return
        setDraft(dRes.data)
        if (virtualIpCompletedThrough(dRes.data.status) < 2) {
          setPageState('needs_step2')
          return
        }
        return Promise.all([
          getVirtualIpTypeRecommendation(workspaceId, dRes.data.id).then((r) => {
            if (!cancelled) setTypeRec(r.data)
          }),
          fetchProposals(dRes.data.id).then((data) => {
            if (cancelled) return
            setPageState('ready')
            if (isBusyStatus(data.status)) schedulePoll(dRes.data.id)
          }),
        ])
      })
      .catch((e) => {
        if (cancelled) return
        setLoadError((e as SaasApiError).message || t('common.loadFailedRefresh'))
        setPageState('error')
      })
    return () => {
      cancelled = true
      if (pollTimer.current) clearTimeout(pollTimer.current)
    }
  }, [workspaceId, fetchProposals, schedulePoll])

  const handleStart = () => {
    if (!draft) return
    setActionLoading(true)
    startVirtualIpProposals(workspaceId, draft.id)
      .then((r) => {
        setProposals(r.data)
        setActionLoading(false)
        if (isBusyStatus(r.data.status)) schedulePoll(draft.id)
      })
      .catch((e) => {
        setActionLoading(false)
        message.error((e as SaasApiError).message || t('step3.startGenerateFailed'))
      })
  }

  const handleRegenerate = () => {
    if (!draft) return
    setActionLoading(true)
    regenerateVirtualIpProposals(workspaceId, draft.id)
      .then((r) => {
        setProposals(r.data)
        setActionLoading(false)
        if (isBusyStatus(r.data.status)) schedulePoll(draft.id)
      })
      .catch((e) => {
        setActionLoading(false)
        message.error((e as SaasApiError).message || t('step3.regenerateFailed'))
      })
  }

  const confirmRegenerate = () => {
    Modal.confirm({
      title: t('step3.regenerateModalTitle'),
      content: t('step3.regenerateModalContent'),
      okText: t('step3.regenerate'),
      cancelText: t('actions:cancel'),
      centered: true,
      onOk: () => handleRegenerate(),
    })
  }

  const handleSelect = (proposalId: string) => {
    if (!draft) return
    const runSelect = () => {
      setSelectingId(proposalId)
      return selectVirtualIpProposal(workspaceId, draft.id, proposalId)
        .then((r) => {
          setDraft(r.data)
          setSelectingId(null)
          if (proposals) {
            setProposals({
              ...proposals,
              selected_proposal_id: proposalId,
            })
          }
        })
        .catch((e) => {
          setSelectingId(null)
          message.error((e as SaasApiError).message || t('common.selectFailed'))
        })
    }
    confirmUpstreamEditIfNeeded({
      fromStep: 3,
      hasDownstreamArtifacts: draft.status === 'positioning_confirmed',
      onConfirm: runSelect,
    })
  }

  if (pageState === 'loading') {
    return (
      <div className="tc-vip-page">
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 8 }} style={{ marginTop: 20 }} />
      </div>
    )
  }

  if (pageState === 'error') {
    return (
      <div className="tc-vip-page">
        <Alert type="error" showIcon message={t('common.loadFailed')} description={loadError} />
      </div>
    )
  }

  if (pageState === 'needs_step2') {
    return (
      <div className="tc-vip-page" data-testid="virtual-ip-step3-needs-step2">
        <PageHeader ipTypeDisplay={null} onRegenerate={confirmRegenerate} showRegenerate={false} />
        <VirtualIpStepper currentStep={3} progress={progress} />
        <Result
          status="info"
          title={t('step3.prerequisiteTitle')} subTitle={t('step3.prerequisiteSubtitle')}
          extra={
            <Button type="primary" onClick={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-2`)}>
              {t('common.backToStep', { step: 2 })}
            </Button>
          }
        />
      </div>
    )
  }

  const status = proposals?.status ?? 'NOT_STARTED'
  const payload = (proposals?.proposals_payload ?? {}) as Record<string, unknown>
  const proposalList = parseProposals(payload)
  const recommendedId =
    typeof payload.recommended_proposal_id === 'string' ? payload.recommended_proposal_id : null
  const ipTypeDisplay = resolveIpTypeDisplay(typeRec, payload)
  const selectedProposalId = draft?.selected_proposal_id ?? proposals?.selected_proposal_id ?? null
  const selectedProposal =
    proposalList.find((p) => p.proposal_id === selectedProposalId) ??
    ((proposals?.selected_proposal as ProposalItem | null) ?? null)
  const hasSelection = Boolean(selectedProposalId)
  const busy = isBusyStatus(status)
  const nextDisabled = !hasSelection || status === 'FAILED' || busy

  let nextDisabledReason = ''
  if (status === 'FAILED') nextDisabledReason = t('step3.generateFailedRetry')
  else if (busy) nextDisabledReason = t('step3.generatingWait')
  else if (!hasSelection) nextDisabledReason = t('step3.selectProposalRequired')

  return (
    <div className="tc-vip-page" data-testid="virtual-ip-step3-page">
      <PageHeader
        ipTypeDisplay={ipTypeDisplay}
        onRegenerate={confirmRegenerate}
        showRegenerate={!isBusyStatus(status) && status !== 'NOT_STARTED'}
      />

      <VirtualIpStepper currentStep={3} progress={progress} />

      <ParentDirectionBanner selected={typeRec?.selected ?? null} />

      <div className="tc-vip-layout">
        <div className="tc-vip-main">
          <ProposalArea
            status={status}
            proposalList={proposalList}
            recommendedId={recommendedId}
            selectedProposalId={selectedProposalId}
            selectingId={selectingId}
            actionLoading={actionLoading}
            errorMessage={proposals?.error_message ?? null}
            errorCode={proposals?.error_code ?? null}
            onSelect={handleSelect}
            onRegenerate={handleRegenerate}
          />
        </div>

        <aside className="tc-vip-aside">
          <StepSidebar
            status={status}
            typeRec={typeRec}
            selectedProposal={selectedProposal}
            actionLoading={actionLoading}
            onStart={handleStart}
            onRegenerate={confirmRegenerate}
            nextDisabled={nextDisabled}
            nextDisabledReason={nextDisabledReason}
            onNext={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-4`)}
            onPrev={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-2`)}
          />
        </aside>
      </div>
    </div>
  )
}

function PageHeader({
  ipTypeDisplay,
  onRegenerate,
  showRegenerate,
}: {
  ipTypeDisplay: string | null
  onRegenerate: () => void
  showRegenerate: boolean
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const navigate = useNavigate()
  const { workspaceId = '' } = useParams()
  return (
    <div className="tc-page-header">
      <div>
        <h1 className="tc-page-title">{t('common.pageTitle')}</h1>
        <div className="tc-vip-step-label">
          <span>STEP 3 / {VIRTUAL_IP_FLOW_TOTAL_STEPS}</span>
          <span className="tc-vip-step-title">{t('step3.title')}</span>
        </div>
        <p className="tc-page-desc">
          {t('step3.proposalsDesc')}
          {ipTypeDisplay ? ` ${t('common.basedOn', { name: ipTypeDisplay })}` : ''}
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
        {showRegenerate && (
          <Button size="small" icon={<ReloadOutlined />} onClick={onRegenerate}>
            {t('step3.regenerate')}
          </Button>
        )}
        <Button size="small" onClick={() => navigate(`/workspace/${workspaceId}/overview`)}>
          {t('common.backToOverview')}
        </Button>
      </div>
    </div>
  )
}

/** VISTOERA §4/§5 — "父级定位" banner. Every value here comes straight
 * from the already-persisted STEP 2 selection snapshot
 * (``draft.selected_ip_type_snapshot_json`` via
 * ``VirtualIpTypeRecommendation.selected.snapshot``) — no new AI call, no
 * frontend re-summarization. If the snapshot is missing a
 * ``one_sentence_positioning`` value for some reason, the t('step3.parentBannerTag') row
 * is simply omitted rather than inventing text. */
function ParentDirectionBanner({ selected }: { selected: VirtualIpSelectedType | null }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const snapshot = selected?.snapshot
  const displayName =
    (snapshot && typeof snapshot.display_name === 'string' && snapshot.display_name) || selected?.ip_type || null
  const score = snapshot && typeof snapshot.suitability_score === 'number' ? snapshot.suitability_score : null
  const positioning =
    snapshot && typeof snapshot.one_sentence_positioning === 'string' ? snapshot.one_sentence_positioning : ''

  if (!displayName) return null

  return (
    <div className="tc-vip-parent-banner" data-testid="vip-parent-direction-banner">
      <div className="tc-vip-parent-banner-row">
        <span className="tc-vip-parent-banner-eyebrow">{t('step3.parentBannerEyebrow')}</span>
      </div>
      <div className="tc-vip-parent-banner-main">
        <div className="tc-vip-parent-banner-type">
          <CheckCircleFilled className="tc-vip-check-icon" />
          <span className="tc-vip-parent-banner-name">{displayName}</span>
          {score !== null && <span className="tc-vip-parent-banner-score">{t('common.fitScore', { score })}</span>}
        </div>
        <span className="tc-vip-parent-banner-tag">{t('step3.parentBannerTag')}</span>
      </div>
      {positioning && <p className="tc-vip-parent-banner-positioning">{positioning}</p>}
      <div className="tc-vip-parent-banner-hint">{t('step3.parentBannerHint')}</div>
    </div>
  )
}

function ProposalArea({
  status,
  proposalList,
  recommendedId,
  selectedProposalId,
  selectingId,
  actionLoading,
  errorMessage,
  errorCode,
  onSelect,
  onRegenerate,
}: {
  status: string
  proposalList: ProposalItem[]
  recommendedId: string | null
  selectedProposalId: string | null
  selectingId: string | null
  actionLoading: boolean
  errorMessage: string | null
  errorCode: string | null
  onSelect: (proposalId: string) => void
  onRegenerate: () => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  return (
    <div className="tc-data-card tc-vip-proposal-area" data-testid="vip-proposals-area">
      <div className="tc-vip-card-title">{t('step3.proposalsTitle', { count: proposalList.length || 4 })}</div>
      <p className="tc-vip-card-desc">{t('step3.proposalsDesc')}</p>

      {status === 'NOT_STARTED' && (
        <div className="tc-vip-empty-state" data-testid="vip-proposals-not-started">
          <UserOutlined className="tc-vip-empty-icon" />
          <p>{t('step3.noProposalsYet')}</p>
          <span className="tc-vip-card-hint">{t('step3.startGenerateHint')}</span>
        </div>
      )}

      {isBusyStatus(status) && (
        <div className="tc-vip-proposals-grid" data-testid="vip-proposals-running">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="tc-vip-proposal-card is-loading">
              <Skeleton active avatar={false} paragraph={{ rows: 5 }} />
            </div>
          ))}
        </div>
      )}

      {status === 'FAILED' && (
        <div className="tc-vip-fail-card" data-testid="vip-proposals-failed">
          <ExclamationCircleFilled className="tc-vip-fail-icon" />
          <div className="tc-vip-fail-title">{t('step3.failTitle')}</div>
          <p className="tc-vip-fail-desc">
            {t('step3.failDescWithContext')}
          </p>
          <Button danger loading={actionLoading} onClick={onRegenerate} icon={<ReloadOutlined />}>
            {t('step3.regenerate')}
          </Button>
          {(errorMessage || errorCode) && (
            <Collapse
              ghost
              className="tc-vip-fail-tech"
              items={[
                {
                  key: 'tech',
                  label: t('common.viewTechDetails'),
                  children: (
                    <code className="tc-vip-fail-tech-text">
                      {errorCode ? `[${errorCode}] ` : ''}
                      {errorMessage || '(no error message)'}
                    </code>
                  ),
                },
              ]}
            />
          )}
        </div>
      )}

      {status === 'STALE' && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningFilled />}
          message={t('step3.staleTitle')} description={t('step3.staleDesc')} action={
            <Button size="small" loading={actionLoading} onClick={onRegenerate} icon={<ReloadOutlined />}>
              {t('step3.regenerate')}
            </Button>
          }
          data-testid="vip-proposals-stale-banner"
          style={{ marginBottom: 12 }}
        />
      )}

      {(status === 'COMPLETED' || status === 'STALE') &&
        (proposalList.length === 0 ? (
          <div className="tc-vip-empty-state" data-testid="vip-proposals-empty-result">
            <UserOutlined className="tc-vip-empty-icon" />
            <p>{t('step3.noProposalsReturned')}</p>
            <Button onClick={onRegenerate} icon={<ReloadOutlined />}>
              {t('step3.regenerate')}
            </Button>
          </div>
        ) : (
          <div className="tc-vip-proposals-grid" data-testid="vip-proposals-result">
            {proposalList.map((proposal, idx) => (
              <ProposalCard
                key={proposal.proposal_id}
                proposal={proposal}
                accent={accentForIndex(idx)}
                index={idx}
                isSelected={selectedProposalId === proposal.proposal_id}
                isRecommended={recommendedId === proposal.proposal_id}
                isSelecting={selectingId === proposal.proposal_id}
                disabledSelecting={Boolean(selectingId) && selectingId !== proposal.proposal_id}
                onSelect={() => onSelect(proposal.proposal_id)}
              />
            ))}
          </div>
        ))}
    </div>
  )
}

function QuickField({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string
}) {
  if (!value) return null
  return (
    <div className="tc-vip-quick-field">
      <span className="tc-vip-quick-field-icon" aria-hidden>
        {icon}
      </span>
      <div className="tc-vip-quick-field-body">
        <span className="tc-vip-quick-field-label">{label}</span>
        <span className="tc-vip-quick-field-value">{value}</span>
      </div>
    </div>
  )
}

function DetailSection({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div className="tc-vip-proposal-detail-section">
      <span className="tc-vip-subfield-title">{heading}</span>
      {children}
    </div>
  )
}

function TagRow({ items }: { items: string[] }) {
  if (items.length === 0) return null
  return (
    <div className="tc-vip-options">
      {items.map((item, idx) => (
        <Tag key={idx} className="tc-vip-analysis-tag">
          {item}
        </Tag>
      ))}
    </div>
  )
}

function ProposalCard({
  proposal,
  accent,
  index,
  isSelected,
  isRecommended,
  isSelecting,
  disabledSelecting,
  onSelect,
}: {
  proposal: ProposalItem
  accent: string
  index: number
  isSelected: boolean
  isRecommended: boolean
  isSelecting: boolean
  disabledSelecting: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const name = typeof proposal.name === 'string' ? proposal.name : t('common.pendingGenerate')
  const subtitle = typeof proposal.subtitle === 'string' ? proposal.subtitle : ''
  const positioningLabel = typeof proposal.positioning_label === 'string' ? proposal.positioning_label : ''
  const oneLiner = typeof proposal.one_liner === 'string' ? proposal.one_liner : ''
  const score = typeof proposal.match_score === 'number' ? proposal.match_score : null
  const coreValueProposition =
    typeof proposal.core_value_proposition === 'string' ? proposal.core_value_proposition : ''
  const contentDirection = asStringArray(proposal.content_direction)
  const commercialDirection = formatCommercialDirection(proposal.commercial_direction)
  const targetAudience = typeof proposal.target_audience === 'string' ? proposal.target_audience : ''
  const differentiation = typeof proposal.differentiation === 'string' ? proposal.differentiation : ''

  const summary = typeof proposal.summary === 'string' ? proposal.summary : ''
  const personaTraits = asStringArray(proposal.persona_traits)
  const platformStrategy = asStringArray(proposal.platform_strategy)
  const visualDirection = typeof proposal.visual_direction === 'string' ? proposal.visual_direction : ''
  const voiceStyle = typeof proposal.voice_style === 'string' ? proposal.voice_style : ''
  const contentTone = asStringArray(proposal.content_tone)
  const riskBoundaries = asStringArray(proposal.risk_boundaries)
  const tags = asStringArray(proposal.tags)
  const traitScores = proposal.trait_scores && typeof proposal.trait_scores === 'object' ? proposal.trait_scores : null
  const speakingStyleSample =
    typeof proposal.speaking_style_sample === 'string' ? proposal.speaking_style_sample : ''
  const recommendationReason =
    typeof proposal.recommendation_reason === 'string' ? proposal.recommendation_reason : ''
  const growthPotential = typeof proposal.growth_potential === 'string' ? proposal.growth_potential : ''

  return (
    <div
      className={`tc-vip-proposal-card is-accent-${accent}${isSelected ? ' is-selected' : ''}${disabledSelecting ? ' is-loading' : ''}`}
      data-testid={`vip-proposal-card-${proposal.proposal_id}`}
      role="button"
      tabIndex={disabledSelecting ? -1 : 0}
      aria-selected={isSelected}
      onClick={() => {
        if (!disabledSelecting) onSelect()
      }}
      onKeyDown={(e) => {
        if (!disabledSelecting && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onSelect()
        }
      }}
    >
      {isSelected && (
        <div className="tc-vip-proposal-card-check" aria-hidden>
          <CheckCircleFilled />
        </div>
      )}

      <div className="tc-vip-proposal-card-head">
        <span className="tc-vip-proposal-index">{String(index + 1).padStart(2, '0')}</span>
        {isRecommended && <span className="tc-vip-proposal-ai-badge">{t('step3.aiRecommended')}</span>}
      </div>

      <div className="tc-vip-proposal-name-row">
        <span className="tc-vip-proposal-name">{name}</span>
        {score !== null && <span className="tc-vip-proposal-score">{score}%</span>}
      </div>
      {subtitle && <p className="tc-vip-proposal-subtitle-text">{subtitle}</p>}
      {positioningLabel && <span className="tc-vip-proposal-label-tag">{positioningLabel}</span>}
      {oneLiner && <p className="tc-vip-proposal-oneliner">「{oneLiner}」</p>}

      {(coreValueProposition || contentDirection.length > 0 || commercialDirection || targetAudience) && (
        <div className="tc-vip-quick-fields">
          <QuickField icon={<AimOutlined />} label={t('fields.corePositioning')} value={coreValueProposition} />
          <QuickField icon={<CompassOutlined />} label={t('fields.contentDirection')} value={contentDirection.join('、')} />
          <QuickField icon={<DollarOutlined />} label={t('fields.businessModel')} value={commercialDirection} />
          <QuickField icon={<TeamOutlined />} label={t('fields.suitableAudience')} value={targetAudience} />
        </div>
      )}

      {differentiation && (
        <div className="tc-vip-proposal-core-diff" data-testid="vip-proposal-core-diff">
          <div className="tc-vip-proposal-core-diff-head">
            <BulbOutlined />
            <span>{t('step3.sectionCoreDifference')}</span>
          </div>
          <p>{differentiation}</p>
        </div>
      )}

      <div className="tc-vip-proposal-detail-list">
        {summary && (
          <DetailSection heading={t('step3.sectionSummary')}>
            <p className="tc-vip-proposal-detail-text">{summary}</p>
          </DetailSection>
        )}
        {personaTraits.length > 0 && (
          <DetailSection heading={t('fields.personaTraits')}>
            <TagRow items={personaTraits} />
          </DetailSection>
        )}
        {platformStrategy.length > 0 && (
          <DetailSection heading={t('fields.platformStrategy')}>
            <TagRow items={platformStrategy} />
          </DetailSection>
        )}
        {visualDirection && (
          <DetailSection heading={t('fields.visualDirection')}>
            <p className="tc-vip-proposal-detail-text">{visualDirection}</p>
          </DetailSection>
        )}
        {voiceStyle && (
          <DetailSection heading={t('step3.sectionLanguageStyle')}>
            <p className="tc-vip-proposal-detail-text">{voiceStyle}</p>
          </DetailSection>
        )}
        {contentTone.length > 0 && (
          <DetailSection heading={t('step3.sectionContentTone')}>
            <TagRow items={contentTone} />
          </DetailSection>
        )}
        {riskBoundaries.length > 0 && (
          <DetailSection heading={t('step3.sectionRiskBoundary')}>
            <div className="tc-vip-options">
              {riskBoundaries.map((r, i) => (
                <Tag key={i} color="warning">
                  {r}
                </Tag>
              ))}
            </div>
          </DetailSection>
        )}
        {tags.length > 0 && (
          <DetailSection heading={t('step3.sectionTags')}>
            <TagRow items={tags} />
          </DetailSection>
        )}
        {traitScores && Object.keys(traitScores).length > 0 && (
          <DetailSection heading={t('step3.sectionTraitScores')}>
            <div className="tc-vip-trait-scores">
              {Object.entries(traitScores).map(([key, val]) => (
                <span key={key} className="tc-vip-trait-score-chip">
                  {key} <strong>{typeof val === 'number' ? val : String(val)}</strong>
                </span>
              ))}
            </div>
          </DetailSection>
        )}
        {speakingStyleSample && (
          <DetailSection heading={t('step3.sectionLanguageSample')}>
            <p className="tc-vip-proposal-speaking-sample">
              <SoundOutlined style={{ marginRight: 6 }} />
              {speakingStyleSample}
            </p>
          </DetailSection>
        )}
        {recommendationReason && (
          <DetailSection heading={t('step3.sectionRecommendReason')}>
            <p className="tc-vip-proposal-detail-text">{recommendationReason}</p>
          </DetailSection>
        )}
        {growthPotential && (
          <DetailSection heading={t('fields.growthPotential')}>
            <span className="tc-vip-growth-chip">
              <RiseOutlined style={{ marginRight: 4 }} />
              {growthPotential ? t(`growthPotential.${growthPotential}`, { defaultValue: growthPotential }) : null}
            </span>
          </DetailSection>
        )}
      </div>

      {isSelecting && (
        <div className="tc-vip-proposal-card-overlay">
          <LoadingOutlined spin />
        </div>
      )}
    </div>
  )
}

/** VISTOERA §18 sidebar restructure: A. 当前{t('common.select')}的大方向（STEP 2）
 * B. 当前 STEP 3 操作（generation status + selected proposal + Next/
 * Previous — both CTAs live here now, none at the page bottom）
 * C. {t('step5.helpTitle')} */
function StepSidebar({
  status,
  typeRec,
  selectedProposal,
  actionLoading,
  onStart,
  onRegenerate,
  nextDisabled,
  nextDisabledReason,
  onNext,
  onPrev,
}: {
  status: string
  typeRec: VirtualIpTypeRecommendation | null
  selectedProposal: ProposalItem | null
  actionLoading: boolean
  onStart: () => void
  onRegenerate: () => void
  nextDisabled: boolean
  nextDisabledReason: string
  onNext: () => void
  onPrev: () => void
}) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const progressStages = t('step3.progressStages', { returnObjects: true }) as string[]
  const confirmChecklist = t('step3.confirmChecklist', { returnObjects: true }) as string[]
  const selected = typeRec?.selected ?? null
  const snapshot = selected?.snapshot
  const displayName =
    (snapshot && typeof snapshot.display_name === 'string' && snapshot.display_name) || selected?.ip_type || null
  const score = snapshot && typeof snapshot.suitability_score === 'number' ? snapshot.suitability_score : null
  const runId = selected?.source_run_id || typeRec?.run_id || null
  const whyItFits = useMemo(() => asStringArray(snapshot?.why_it_fits), [snapshot])

  const badge = proposalStatusBadge(status, t)
  const differentiation =
    selectedProposal && typeof selectedProposal.differentiation === 'string' ? selectedProposal.differentiation : ''

  return (
    <>
      {displayName && (
        <div className="tc-data-card tc-vip-sidebar-section" data-testid="vip-sidebar-selected-type">
          <span className="tc-vip-card-title">{t('step3.sidebarSelectedType')}</span>
          <div className="tc-vip-selected-summary-value">
            <CheckCircleFilled className="tc-vip-check-icon" /> {displayName}
            {score !== null && <span className="tc-vip-sidebar-score">{t('common.fitScore', { score })}</span>}
          </div>
          {runId && <div className="tc-vip-card-hint">Run ID: {runId.slice(0, 8)}…</div>}
        </div>
      )}

      {whyItFits.length > 0 && (
        <div className="tc-data-card tc-vip-sidebar-section" data-testid="vip-sidebar-why-fits">
          <span className="tc-vip-card-title">{t('step2.selectionReasonTitle')}</span>
          <ul className="tc-vip-check-list">
            {whyItFits.map((reason, idx) => (
              <li key={idx}>
                <CheckCircleFilled className="tc-vip-check-icon" /> {reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="tc-data-card tc-vip-sidebar-section" data-testid="vip-sidebar-next-actions">
        <div className="tc-vip-preview-panel-head">
          <span className="tc-vip-card-title">{t('step3.sidebarNextActions')}</span>
          <span className={`tc-vip-status-badge ${badge.className}`}>{badge.text}</span>
        </div>

        {status === 'NOT_STARTED' && (
          <div className="tc-vip-preview-body">
            <p className="tc-vip-card-desc">{t('step3.sidebarStartDesc')}</p>
            <p className="tc-vip-card-hint">{t('step3.sidebarStartHint')}</p>
            <Button type="primary" block loading={actionLoading} onClick={onStart}>
              {t('step3.startGenerate')}
            </Button>
          </div>
        )}

        {isBusyStatus(status) && (
          <div className="tc-vip-preview-body">
            <p className="tc-vip-card-desc">
              <LoadingOutlined spin style={{ marginRight: 6 }} />
              {t('step3.generatingProposals')}
            </p>
            <ul className="tc-vip-stage-list">
              {progressStages.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          </div>
        )}

        {status === 'FAILED' && (
          <div className="tc-vip-preview-body">
            <p className="tc-vip-card-desc">{t('step3.failTitle')}</p>
            <p className="tc-vip-card-hint">{t('step3.sidebarFailHint')}</p>
            <Button danger block loading={actionLoading} onClick={onRegenerate} icon={<ReloadOutlined />}>
              {t('step3.regenerate')}
            </Button>
          </div>
        )}

        {status === 'STALE' && (
          <div className="tc-vip-preview-body">
            <p className="tc-vip-card-desc">{t('step3.sidebarStaleDesc')}</p>
            <p className="tc-vip-card-hint">{t('step3.sidebarStaleHint')}</p>
            <Button block loading={actionLoading} onClick={onRegenerate} icon={<ReloadOutlined />}>
              {t('step3.regenerateProposals')}
            </Button>
          </div>
        )}

        {selectedProposal ? (
          <div className="tc-vip-selected-summary">
            <span className="tc-vip-subfield-title">{t('step3.sidebarSelectedProposal')}</span>
            <div className="tc-vip-selected-summary-value">
              {selectedProposal.name ?? selectedProposal.proposal_id}
            </div>
            {differentiation && <p className="tc-vip-card-hint">{differentiation}</p>}
          </div>
        ) : (
          (status === 'COMPLETED' || status === 'STALE') && (
            <p className="tc-vip-card-hint">{t('step3.selectProposalFirst')}</p>
          )
        )}

        <div className="tc-vip-subfield">
          <span className="tc-vip-subfield-title">{t('step3.sidebarNextConfirm')}</span>
          <ul className="tc-vip-check-list">
            {confirmChecklist.map((label) => (
              <li key={label}>
                <CheckCircleFilled className="tc-vip-check-icon" /> {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="tc-vip-next-cta">
          {nextDisabled ? (
            <Tooltip title={nextDisabledReason}>
              <span>
                <Button type="primary" block disabled data-testid="vip-step3-next-button">
                  {t('step3.nextConfirm')} →
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button type="primary" block onClick={onNext} data-testid="vip-step3-next-button">
              {t('step3.nextConfirm')} →
            </Button>
          )}
          <Button block onClick={onPrev} icon={<ArrowLeftOutlined />} data-testid="vip-step3-prev-button">
            {t('step3.prevReselectType')}
          </Button>
        </div>
      </div>

      <div className="tc-data-card tc-vip-sidebar-section tc-vip-help-block" data-testid="vip-sidebar-help">
        <span className="tc-vip-card-title">
          <QuestionCircleOutlined style={{ marginRight: 6 }} />
          {t('step5.helpTitle')}
        </span>
        <p className="tc-vip-card-hint">
          <SafetyCertificateOutlined style={{ marginRight: 4 }} />
          {t('step3.helpCompareText')}
        </p>
      </div>
    </>
  )
}
